import type { Team, TeamMember } from "@repo/types";
import { Formik } from "formik";
import * as yup from "yup";
import { Input } from "@repo/ui/components/Base/Input";
import { Button } from "@repo/ui/components/Base/Button";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { Separator } from "@repo/ui/components/Base/Separator";
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@repo/ui/components/Base/Dialog";
import { PlusCircleIcon } from "lucide-react";
import { CreateUserForm } from "../users/CreateUserForm";
import { useState } from "react";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { AddressFormField } from "../users/AddressFormField";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { TimeField } from "@repo/ui/components/Base/TimeField";
import { Time } from "@internationalized/date";
import { Label } from "@repo/ui/components/Base/Label";
import { useUserList } from "@/hooks/queryHooks/userHooks";
import { teamRepo } from "@/repositories/teamRepo";

interface TeamFormProps {
  teamFormData?: Team;
  tenantId: string;
  handleCancel?: () => void;
  handleComplete: () => void;
  cancelButtonText?: string;
  completeButtonText?: string;
  withinDialog?: boolean;
}

export const TeamForm = ({
  teamFormData,
  tenantId,
  handleCancel,
  handleComplete,
  cancelButtonText,
  completeButtonText,
  withinDialog,
}: TeamFormProps) => {
  useBlockUnload();

  const { data: usersData } = useUserList(tenantId);

  const [open, setOpen] = useState(false);
  const handleOpenChange = () => setOpen(!open);

  const validationSchema = yup.object({
    name: yup.string().required("Team name is required"),
    adminUsers: yup.array().min(1, "Team must have at least one admin"),
  });

  return (
    <Formik
      initialValues={teamFormData ? { ...teamFormData } : teamRepo.init()}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        if (!tenantId) {
          console.log("No tenant ID", tenantId);
          return;
        }

        if (teamFormData) {
          await teamRepo.update(tenantId, values.id, { ...values });
        } else {
          await teamRepo.create(tenantId, { ...values });
        }

        handleComplete();
        resetForm();
      }}
      validateOnChange
    >
      {({
        values,
        setFieldValue,
        handleSubmit,
        handleBlur,
        handleChange,
        errors,
        touched,
        isSubmitting,
      }) => {
        const footer = (
          <>
            {handleCancel && (
              <Button
                variant="destructive"
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  handleCancel();
                }}
              >
                {cancelButtonText ? cancelButtonText : "Cancel"}
              </Button>
            )}
            <Button
              type="submit"
              disabled={
                values.name === "" ||
                values.adminUsers.length === 0 ||
                isSubmitting
              }
            >
              {completeButtonText ? completeButtonText : "Save"}
            </Button>
          </>
        );

        const form = (
          <div className="flex flex-col gap-y-4">
            <Dialog open={open} onOpenChange={handleOpenChange}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create a New User</DialogTitle>
                </DialogHeader>
                <CreateUserForm
                  tenantId={tenantId}
                  handleCancel={handleOpenChange}
                  handleComplete={handleOpenChange}
                  withinDialog
                />
              </DialogContent>
            </Dialog>

            <Input
              disabled={isSubmitting}
              label="*Team Name"
              id="name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.name && touched.name}
              errorMessage={errors.name}
            />

            {!teamFormData && (
              <>
                <Separator />
                <div className="flex flew-row justify-between">
                  <h3>All teams must have at least one admin.</h3>
                  <Tooltip label="Create a new user" asChild>
                    <button
                      disabled={isSubmitting}
                      className="flex flex-row gap-2 p-2 rounded-lg hover:scale-110 duration-100 shadow-2xl"
                      onClick={() => setOpen(true)}
                    >
                      <PlusCircleIcon className="text-primary" />
                    </button>
                  </Tooltip>
                </div>
              </>
            )}

            {!teamFormData && (
              <SelectInput
                label="Team Admin"
                disabled={isSubmitting}
                placeholder="Select an Admin"
                onValueChange={async (value) => {
                  if (value === "new") {
                    setOpen(true);
                    return;
                  }

                  const userSelected = usersData?.find((u) => u.id === value);
                  if (userSelected) {
                    setFieldValue("allUsers", [value]);
                    setFieldValue("adminUsers", [value]);

                    const teamMember: TeamMember = {
                      userId: value,
                      role: "admin",
                      title: "",
                      expires: false,
                    };
                    setFieldValue("teamMembers", [teamMember]);
                  }
                }}
                value={values.adminUsers[0] ?? ""}
                options={[
                  ...(usersData?.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      <div>
                        {`${user.firstName} ${user.lastName}`}{" "}
                        <span className="ml-2 text-xs text-right">
                          {user.email}
                        </span>
                      </div>
                    </SelectItem>
                  )) ?? []),
                  <SelectItem key="new" value="new">
                    <div>+ Create New User</div>
                  </SelectItem>,
                ]}
              />
            )}

            <Separator />

            <AddressFormField
              disabled={isSubmitting}
              address={values.address}
              fieldName="address"
              setFieldValue={setFieldValue}
              singleType
            />

            <Separator />

            <div className="flex flex-col gap-2">
              <Label label="Default Meeting Time" />

              <div className="flex flex-row gap-2 items-center">
                <span>From</span>
                <TimeField
                  className="w-24"
                  disabled={isSubmitting}
                  disableHasTimeChange
                  value={
                    new Time(
                      values?.defaultStartTime?.hours ?? 10,
                      values?.defaultStartTime?.minutes ?? 0
                    )
                  }
                  onChange={(value) => {
                    setFieldValue("defaultStartTime", {
                      hours: value?.hour ?? 10,
                      minutes: value?.minute ?? 0,
                    });
                  }}
                />
                <span>To</span>
                <TimeField
                  className="w-24"
                  disabled={isSubmitting}
                  disableHasTimeChange
                  value={
                    new Time(
                      values?.defaultEndTime?.hours ?? 11,
                      values?.defaultEndTime?.minutes ?? 0
                    )
                  }
                  onChange={(value) => {
                    setFieldValue("defaultEndTime", {
                      hours: value?.hour ?? 11,
                      minutes: value?.minute ?? 0,
                    });
                  }}
                />
              </div>
            </div>

            {!withinDialog && (
              <div className="flex flex-row justify-between">{footer}</div>
            )}
          </div>
        );

        return (
          <form onSubmit={handleSubmit}>
            {withinDialog ? (
              <>
                <DialogBody>{form}</DialogBody>
                <DialogFooter className="justify-between">
                  {footer}
                </DialogFooter>
              </>
            ) : (
              form
            )}
          </form>
        );
      }}
    </Formik>
  );
};
